import { BBox } from "geojson";
import { InferenceCollection } from "../types/inferences";
import { convertPolygonToPointAndBbox } from "../utils";

export type GetInferencesQuery = {
  selectedDates: Date[];
  bbox: BBox | undefined;
  validated: boolean | undefined;
  is_whale?: boolean,
  min_confidence?: number,
  min_coastline_distance?: number,
  asPoints: boolean | undefined
  subResource: "user" | "" | undefined;
}

export type GetSceneInferencesFilters = {
  validated?: boolean,
  is_valid?: boolean,
  is_whale?: boolean,
  min_confidence?: number,
  min_coastline_distance?: number,
  composited?: boolean,
  flagged?: boolean,
}

type HeadersType = {
  Authorization?: string;
  "Content-Type": string;
}

export function getInferences({
  selectedDates,
  bbox,
  validated,
  is_whale,
  min_confidence,
  min_coastline_distance,
  asPoints = false,
  subResource = "",
}: GetInferencesQuery): Promise<InferenceCollection> {
  const headersObject:HeadersType = {
    "Content-Type": "application/json"
  };
  const token = localStorage.getItem("token");
  if (token) {
    headersObject.Authorization = `Bearer ${token}`;
  }
  const headers = new Headers(headersObject);

  const queryParams = [
    ["scene_start_date", selectedDates[0].toISOString().split("T")[0]],
    ["scene_end_date", selectedDates[1].toISOString().split("T")[0]],
  ];
  asPoints && queryParams.push(["as_points", "true"]);
  bbox && queryParams.push(["bbox", bbox.join(",")]);
  validated && queryParams.push(["validated", `${validated}`]);
  is_whale && queryParams.push(["is_whale", `${is_whale}`]);
  min_confidence && queryParams.push(["min_confidence", `${min_confidence}`]);
  min_coastline_distance && queryParams.push(["min_coastline_distance", `${min_coastline_distance}`]);

  const query = new URLSearchParams(queryParams);
  return fetch(
    `${process.env.REACT_APP_API_BASE}/inferences/${subResource}?${query.toString()}`,
    { headers }
  )
    .then(r => {
      if (!r.ok) throw new Error("Unable to start get scenes.");
      return r.json();
    })
    .then(convertPolygonToPointAndBbox);
}

export function getSceneInferences(sceneId: string, filters?: GetSceneInferencesFilters): Promise<InferenceCollection> {
  const headersObject:HeadersType = {
    "Content-Type": "application/json"
  };
  const token = localStorage.getItem("token");
  if (token) {
    headersObject.Authorization = `Bearer ${token}`;
  }
  const headers = new Headers(headersObject);

  const queryParams =  filters
    ? Object.entries(filters).filter((i) => i[1] !== undefined).map((i) => [i[0], `${i[1]}`])
    : [];

  const query = new URLSearchParams(queryParams);
  return fetch(
    `${process.env.REACT_APP_API_BASE}/scenes/${sceneId}/inferences?${query.toString()}`,
    { headers }
  )
    .then(r => {
      if (!r.ok) throw new Error("Unable to start get scenes.");
      return r.json();
    })
    .then(convertPolygonToPointAndBbox);
}

export function invalidate(sceneId: string, inferenceId: number) {
  const token = localStorage.getItem("token");
  const headers = new Headers({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  });

  return fetch(`${process.env.REACT_APP_API_BASE}/scenes/${sceneId}/inferences/${inferenceId}/validation`, {
    method: "DELETE",
    headers,
  })
    .then(r => {
      if (!r.ok) throw new Error("Unable to invalidate inference.");
      return r.json();
    });
}

export function validate(sceneId: string, inferenceId: number, isValid: boolean) {
  const token = localStorage.getItem("token");
  const headers = new Headers({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  });

  return fetch(`${process.env.REACT_APP_API_BASE}/scenes/${sceneId}/inferences/${inferenceId}/validation`, {
    method: "POST",
    headers,
    body: JSON.stringify({ is_valid: isValid })
  })
    .then(r => {
      if (!r.ok) throw new Error("Unable to validate inference.");
      return r.json();
    });
}

export function updateInference(
  sceneId: string,
  inferenceId: number,
  notes?: string,
  selected_class?: string | null,
  flagged?: boolean
) {
  const token = localStorage.getItem("token");
  const headers = new Headers({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  });

  const body: { notes?: string; selected_class?: string | null; flagged?: boolean } = {};
  if (notes !== undefined) body.notes = notes;
  if (selected_class !== undefined) body.selected_class = selected_class;
  if (flagged !== undefined) body.flagged = flagged;

  return fetch(
    `${process.env.REACT_APP_API_BASE}/scenes/${sceneId}/inferences/${inferenceId}`,
    {
      method: "PATCH",
      headers,
      body: JSON.stringify(body),
    }
  ).then((r) => {
    if (!r.ok) throw new Error("Unable to update inference.");
    return r.json();
  });
}


export type BatchUpdateParams = {
  ids: number[];
  is_valid?: boolean;
  validation_date?: string;
  selected_class?: string | null;
  composite_count?: number;
  notes?: string;
  flagged?: boolean;
}

export function batchUpdateInferences(sceneId: string, params: BatchUpdateParams): Promise<unknown> {
  const token = localStorage.getItem("token");
  const headers = new Headers({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  });

  return fetch(
    `${process.env.REACT_APP_API_BASE}/scenes/${sceneId}/inferences/batch_update`,
    {
      method: "PATCH",
      headers,
      body: JSON.stringify(params),
    }
  ).then((r) => {
    if (!r.ok) throw new Error("Unable to batch update inferences.");
    return r.json();
  });
}
